import React from 'react'
import styled from 'styled-components'
import breakpoint from 'tools/styled-components-breakpoint'
import Component from 'decorators/Component'
import Flex from 'components/Flex'
import Form from 'components/Form'
import Image from 'components/Image'
import { H4 } from 'components/Typo'
import { formatDiff } from 'tools/number'
import { margin, marginSizes, borderRadius, borderRadiusSizes, colorConstants } from 'theme/default'
import { status as productStatus } from 'tools/product'
import { Header, ContentContainer, ItemContainer } from './ContentVariantElements'

const SelectedQuantity = styled.span`
  ${props => !props.satisfied ? `color: ${colorConstants.red};` : ''}
`

const OptionItemContainer = styled(ItemContainer).attrs((props) => ({
  justifyContent: 'flex-start'
}))`
  && {
    height: auto;
  }
`

const getOptionIdentifier = ({ option, variant } = {}) => `subitems.${option.id}.${variant.id}`

const getQuantityFromFormValues = (values, name) => {
  const path = name.split('.')
  return path.reduce((acc, cur) => acc !== undefined ? acc[cur] : undefined, values)
}

const getSubtitle = ({ t, minimumQuantity, maximumQuantity }) => {
  if (minimumQuantity === maximumQuantity) {
    return t('Select _maximumQuantity_', { maximumQuantity })
  } if (minimumQuantity === 0) {
    return t('Select up to _maximumQuantity_', { maximumQuantity })
  } else {
    return t('Select at least _minimumQuantity_ up to a maximum of _maximumQuantity_', { minimumQuantity, maximumQuantity })
  }
}

export const OptionHeader = Component()(({
  t,
  option = {},
  show = true,
  hideArrow = false,
  touched = false,
  formatMoney = x => x.toString()
}) => {
  const { description, minimumQuantity = 0, maximumQuantity = 0, selectedQuantity = 0, totalSubPrice = 0 } = option
  const isMinQuantitySatisfied = minimumQuantity <= selectedQuantity
  const subtitle = getSubtitle({ t, minimumQuantity, maximumQuantity })

  return (
    <Header
      show={show}
      hideArrow={hideArrow}
      title={description}
      subtitle={subtitle}
      body={(
        <SelectedQuantity satisfied={isMinQuantitySatisfied}>
          {t('_quantity_ selected', { quantity: selectedQuantity || '0' })}
        </SelectedQuantity>
      )}
      bodySubText={totalSubPrice === 0 ? undefined : formatDiff({ value: totalSubPrice, formatter: formatMoney })}
      error={touched && !isMinQuantitySatisfied} />
  )
})

export const OptionContent = Component()(({
  t,
  show = true,
  withInput = false,
  option = {},
  subItemVariantsKey = 'variants',
  formatMoney = x => x.toString(),
  renderImage = x => null,
  calculateOptionDiff = (x, y) => 0
}) => {
  const { maximumQuantity, selectedQuantity = 0 } = option
  const variants = option[subItemVariantsKey]
  const remainingQuantity = maximumQuantity - selectedQuantity
  const disableIncrease = remainingQuantity === 0

  const onQuantityChange = ({ name, value: nextValue, values: prevValues, setFieldValue }, { variantId }) => {
    // check if there is an existing item and get the previous value
    const prevValue = name.split('.').reduce((object, key) => object && object[key], prevValues)

    // if there is an existing item, check if the selectedQuantity plus the additionalQuantity is less than the maximum quantity
    // otherwise check if selected quantity plus the newly added quantity is less than the maximum quantity
    if (selectedQuantity + (prevValue ? (nextValue - prevValue) : (nextValue)) <= maximumQuantity) {
      setFieldValue(name, nextValue)
    }
  }

  return (
    <ContentContainer show={show}>
      {(variants || []).map((variant, key) => {
        const diff = calculateOptionDiff(option.id, variant.id)
        const disabled = variant.status !== undefined && variant.status !== productStatus.AVAILABLE
        return (
          <OptionItemContainer key={key} disabled={disabled} last={key === variants.length - 1}>
            {renderImage({ variant })}
            <Flex flexDirection='column' alignItems='flex-start' flexGrow={1}>
              <H4 marginTop='none' marginBottom={marginSizes.small}>{variant.variantName}</H4>
              <Flex alignSelf='stretch' alignItems='center' justifyContent='space-between'>
                <Form.QuantitySelector
                  withInput={withInput}
                  onChange={(formProps) => onQuantityChange(formProps, { variantId: variant.id })}
                  disabled={disabled}
                  name={getOptionIdentifier({ option, variant })}
                  disableIncrease={disableIncrease}
                  getValue={getQuantityFromFormValues}
                  resetOnUnmount={false} />
                <H4 marginTop={marginSizes.extraSmall} marginBottom={marginSizes.extraSmall} data-testid={`text.priceDiff.${key}`}>
                  {disabled ? t('Not available') : diff > 0 && !disableIncrease && formatDiff({ value: diff, formatter: formatMoney })}
                </H4>
              </Flex>
            </Flex>
          </OptionItemContainer>
        )
      })}
    </ContentContainer>
  )
})

export const OptionImage = styled(Image).attrs(() => ({
  objectFit: 'cover'
}))`
  height: 70px;
  width: 70px;
  margin-right: ${margin[marginSizes.small]};
  border-radius: ${borderRadius[borderRadiusSizes.small]};
  ${breakpoint('desktop')`
    margin-right: ${margin[marginSizes.medium]};
  `}
`
